<script setup>
/**
 * ImmersiveCelebration
 * One-shot celebration overlay that pops over the immersive pane, holds, and
 * then FLIES into the checkpoint dot it just unlocked on the steps ladder
 * (host passes `flyTargetEl` when the fly phase should start; `fly-done` fires
 * when the badge has been absorbed). When no ladder dot exists on screen the
 * host simply hides it and the badge fades out at the center (old behavior).
 * Two themes:
 *   - discount: green percent tag + "-N%"  (amount-discount tier reached)
 *   - promo:    gold price tag + "₪X"      (madregot promo step reached)
 * Driven entirely by props (show + kind + label + flyTargetEl); the host owns
 * timing and one-shot semantics. Never blocks touch (pointer-events: none).
 */
import { ref, watch, onBeforeUnmount } from 'vue'

const props = defineProps({
  show: { type: Boolean, default: false },
  // 'discount' (green, "-N%") or 'promo' (gold, "₪X").
  kind: { type: String, default: 'discount' },
  // The short bold text rendered inside the badge (already localized).
  label: { type: String, default: '' },
  // aria-label for the live region (already localized by the host).
  ariaLabel: { type: String, default: '' },
  // The ladder checkpoint dot (DOM element) the badge should fly into. The
  // host sets it AFTER the pop+hold; null keeps the badge held at center.
  flyTargetEl: { type: Object, default: null },
})

const emit = defineEmits(['fly-done'])

// Must match the imm-celebrate-fly keyframe duration below.
const FLY_MS = 500

const badgeRef = ref(null)
const isFlying = ref(false)
const flyStyle = ref(null)
let flyDoneTimer = null

// Fly phase: measure badge → dot (both viewport rects, so ancestor transforms
// like the pane track's translate are already accounted for) and hand the
// deltas to CSS. The timer (not transitionend) drives fly-done — it can't be
// swallowed by a background tab.
watch(
  () => props.flyTargetEl,
  (el) => {
    if (!el || !props.show || isFlying.value) return
    const badge = badgeRef.value
    if (!badge) {
      emit('fly-done')
      return
    }
    const b = badge.getBoundingClientRect()
    const d = el.getBoundingClientRect()
    if (!b.width || !d.width) {
      emit('fly-done')
      return
    }
    const dx = d.left + d.width / 2 - (b.left + b.width / 2)
    const dy = d.top + d.height / 2 - (b.top + b.height / 2)
    flyStyle.value = {
      '--fly-x': `${dx}px`,
      '--fly-y': `${dy}px`,
      // Shrink toward the dot's size; the opacity fade finishes the absorb.
      '--fly-scale': Math.max(0.08, Math.min(0.3, d.height / b.height)),
    }
    isFlying.value = true
    flyDoneTimer = setTimeout(() => emit('fly-done'), FLY_MS + 60)
  }
)

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      // Reset fly state on SHOW (not hide): the hidden element is mid-leave
      // fade at hide time, and yanking .is-flying there would restart the pop
      // keyframe on it — a visible ghost pop. By the next show the old node is
      // frozen by <Transition>, so the fresh badge mounts clean.
      isFlying.value = false
      flyStyle.value = null
    } else if (flyDoneTimer) {
      // Canceled mid-flight (retrigger / teardown) — don't emit a stale done.
      clearTimeout(flyDoneTimer)
      flyDoneTimer = null
    }
  }
)

onBeforeUnmount(() => {
  if (flyDoneTimer) clearTimeout(flyDoneTimer)
})
</script>

<template>
  <Transition name="imm-celebrate">
    <div
      v-if="show"
      class="imm-celebrate"
      :class="[`imm-celebrate--${kind}`, { 'is-flying': isFlying }]"
      role="status"
      :aria-label="ariaLabel"
    >
      <span class="imm-celebrate-glow" aria-hidden="true"></span>
      <span class="imm-celebrate-spark imm-celebrate-spark-1" aria-hidden="true"></span>
      <span class="imm-celebrate-spark imm-celebrate-spark-2" aria-hidden="true"></span>
      <span class="imm-celebrate-spark imm-celebrate-spark-3" aria-hidden="true"></span>
      <span class="imm-celebrate-spark imm-celebrate-spark-4" aria-hidden="true"></span>
      <span class="imm-celebrate-spark imm-celebrate-spark-5" aria-hidden="true"></span>
      <span ref="badgeRef" class="imm-celebrate-badge" :style="flyStyle">
        <!-- Discount: percent icon -->
        <svg
          v-if="kind === 'discount'"
          class="imm-celebrate-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="19" y1="5" x2="5" y2="19" />
          <circle cx="6.5" cy="6.5" r="2.5" />
          <circle cx="17.5" cy="17.5" r="2.5" />
        </svg>
        <!-- Promo: price tag icon -->
        <svg
          v-else
          class="imm-celebrate-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"
          />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
        <span class="imm-celebrate-amount">{{ label }}</span>
      </span>
    </div>
  </Transition>
</template>

<style scoped>
/* Pops over the pane center, holds, then flies to the ladder checkpoint.
   Never blocks touch. */
.imm-celebrate {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 7;
  pointer-events: none;
}

/* ── Theme tokens: green (discount tier) vs gold (promo step) ── */
.imm-celebrate--discount {
  --celebrate-accent: #34d399;
  --celebrate-accent-strong: #a7f3d0;
  --celebrate-glow-0: rgba(16, 185, 129, 0.5);
  --celebrate-glow-1: rgba(16, 185, 129, 0.26);
  --celebrate-glow-2: rgba(16, 185, 129, 0);
  --celebrate-border: rgba(52, 211, 153, 0.75);
  --celebrate-ring: rgba(52, 211, 153, 0.35);
  --celebrate-amount-shadow: rgba(16, 185, 129, 0.5);
  --celebrate-spark-0: #a7f3d0;
  --celebrate-spark-1: #10b981;
  --celebrate-spark-2: rgba(16, 185, 129, 0);
}

.imm-celebrate--promo {
  --celebrate-accent: #fcd34d;
  --celebrate-accent-strong: #fde68a;
  --celebrate-glow-0: rgba(252, 211, 77, 0.55);
  --celebrate-glow-1: rgba(251, 191, 36, 0.28);
  --celebrate-glow-2: rgba(251, 191, 36, 0);
  --celebrate-border: rgba(252, 211, 77, 0.75);
  --celebrate-ring: rgba(252, 211, 77, 0.35);
  --celebrate-amount-shadow: rgba(251, 191, 36, 0.5);
  --celebrate-spark-0: #fde68a;
  --celebrate-spark-1: #fbbf24;
  --celebrate-spark-2: rgba(251, 191, 36, 0);
}

/* Soft themed radial glow that pulses behind the badge */
.imm-celebrate-glow {
  position: absolute;
  width: 230px;
  height: 230px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    var(--celebrate-glow-0) 0%,
    var(--celebrate-glow-1) 38%,
    var(--celebrate-glow-2) 70%
  );
  filter: blur(2px);
  animation: imm-celebrate-glow 1.5s ease-out forwards;
}

/* Icon + amount badge: spring-ish pop with a slight rise, then HOLDS visible
   (the fly phase or the host's hide fade takes it from there). */
.imm-celebrate-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 999px;
  /* Dark glass pill stays in BOTH themes — it guarantees the green/gold accent
     contrast on any background; only the drop shadow is themed (softer on the
     light overlay via --imm-celebrate-shadow). */
  background: rgba(15, 23, 42, 0.72);
  border: 1.5px solid var(--celebrate-border);
  box-shadow:
    0 10px 30px var(--imm-celebrate-shadow, rgba(0, 0, 0, 0.5)),
    0 0 26px var(--celebrate-ring);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--celebrate-accent);
  animation: imm-celebrate-pop 0.75s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

/* Fly phase: a KEYFRAME animation (not a transition — swapping a filled
   animation for a transition on the same property snaps instead of animating
   in Chrome) that replaces the pop keyframe and carries the badge into the
   checkpoint dot: shrink all the way, fade only near the end so the pill
   visibly "enters" the dot. Duration must match FLY_MS in the script. */
.imm-celebrate.is-flying .imm-celebrate-badge {
  animation: imm-celebrate-fly 0.5s cubic-bezier(0.6, 0.05, 0.3, 1) forwards;
}

@keyframes imm-celebrate-fly {
  0% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
  72% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate3d(var(--fly-x, 0px), var(--fly-y, 0px), 0) scale(var(--fly-scale, 0.15));
  }
}

.imm-celebrate.is-flying .imm-celebrate-glow {
  animation: imm-celebrate-glow-out 0.25s ease forwards;
}

@keyframes imm-celebrate-glow-out {
  0% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
  }
}

/* Sparks are one-shot and essentially spent by fly time — just drop them. */
.imm-celebrate.is-flying .imm-celebrate-spark {
  animation: none;
  opacity: 0;
}

.imm-celebrate-icon {
  width: 30px;
  height: 30px;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.45));
}

.imm-celebrate-amount {
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1;
  color: var(--celebrate-accent-strong);
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 10px var(--celebrate-amount-shadow);
}

/* A few CSS-only sparkle dots that fly outward from center */
.imm-celebrate-spark {
  position: absolute;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    var(--celebrate-spark-0) 0%,
    var(--celebrate-spark-1) 60%,
    var(--celebrate-spark-2) 100%
  );
  opacity: 0;
  animation: imm-celebrate-spark 1.2s ease-out forwards;
}

.imm-celebrate-spark-1 {
  --sx: -86px;
  --sy: -54px;
  animation-delay: 0.06s;
}
.imm-celebrate-spark-2 {
  --sx: 92px;
  --sy: -40px;
  animation-delay: 0.12s;
}
.imm-celebrate-spark-3 {
  --sx: -70px;
  --sy: 60px;
  animation-delay: 0.02s;
}
.imm-celebrate-spark-4 {
  --sx: 78px;
  --sy: 64px;
  animation-delay: 0.16s;
}
.imm-celebrate-spark-5 {
  --sx: 0px;
  --sy: -96px;
  animation-delay: 0.1s;
}

@keyframes imm-celebrate-glow {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  30% {
    opacity: 1;
    transform: scale(1.04);
  }
  70% {
    opacity: 0.85;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.08);
  }
}

@keyframes imm-celebrate-pop {
  0% {
    opacity: 0;
    transform: translateY(14px) scale(0.55);
  }
  70% {
    opacity: 1;
    transform: translateY(-4px) scale(1.12);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes imm-celebrate-spark {
  0% {
    opacity: 0;
    transform: translate(0, 0) scale(0.4);
  }
  25% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(var(--sx), var(--sy)) scale(1);
  }
}

/* Transition wrapper — the keyframes own the motion; keep leave a clean fade
   (after a fly the badge is already invisible, so leave is a no-op visually). */
.imm-celebrate-enter-active {
  transition: opacity 0.1s ease;
}
.imm-celebrate-leave-active {
  transition: opacity 0.3s ease;
}
.imm-celebrate-enter-from,
.imm-celebrate-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .imm-celebrate-glow,
  .imm-celebrate-badge,
  .imm-celebrate-spark {
    animation: none;
  }
  /* Reduced motion: simple static badge + plain fade, no pop/sparkles/fly
     (the host never starts the fly phase under reduced motion) */
  .imm-celebrate-glow {
    opacity: 0.6;
    transform: scale(1);
  }
  .imm-celebrate-badge {
    opacity: 1;
    transform: none;
  }
  .imm-celebrate-spark {
    display: none;
  }
}
</style>
